import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Handshake, PackageCheck, Truck, ShieldCheck, BadgeCheck, Landmark, CalendarDays, Receipt, Loader2, Download, Sprout } from "lucide-react";
import { COMPANY, CONTACT, ASSETS } from "@/data/site";
import { Reveal, Overline } from "@/components/Reveal";
import { submitManufacturerInquiry } from "@/lib/api";

const FACTS = [
  { icon: Landmark, label: "Paid-Up Capital", value: COMPANY.paidUpCapital, testid: "cg-fact-capital" },
  { icon: CalendarDays, label: "Incorporated On", value: COMPANY.incorporated, testid: "cg-fact-incorporated" },
  { icon: Receipt, label: "MSME (Udyam) Number", value: COMPANY.msme, testid: "cg-fact-msme" },
];

const CG_FOUNDERS = [
  {
    name: "Mr. Anuj Agrawal",
    role: "Director, Ananya Consumer Goods Pvt. Ltd.",
    note: "Leads sourcing, manufacturer partnerships and quality — the discipline behind every Ananya Gold pack.",
  },
  {
    name: "Mrs. Prachi Agrawal",
    role: "Director, Ananya Consumer Goods Pvt. Ltd.",
    note: "Drives branding, sales and the retail network that carries Ananya Gold to market.",
  },
];

const MODEL = [
  {
    n: "01",
    icon: Handshake,
    title: "Partner with Manufacturers",
    body: "We work with small and emerging manufacturers who make quality products but lack a brand or distribution of their own.",
  },
  {
    n: "02",
    icon: PackageCheck,
    title: "Repackage & Brand",
    body: "Goods are quality-checked, repackaged and brought to market under our own Ananya Gold branding — compliant, shelf-ready and consistent.",
  },
  {
    n: "03",
    icon: Truck,
    title: "Sell Through Our Network",
    body: "Finished products move through our retail and distribution network, giving manufacturers reach and retailers a dependable branded supply.",
  },
];

const MFR_INITIAL = {
  name: "",
  factory_name: "",
  location: "",
  product_type: "Soaps & Bathing Bars",
  monthly_capacity: "",
  phone: "",
  email: "",
  message: "",
};

export default function ConsumerGoods() {
  const [form, setForm] = useState(MFR_INITIAL);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitManufacturerInquiry(form);
      toast.success("Inquiry sent. Our sourcing team will contact you to discuss your products.");
      setForm(MFR_INITIAL);
    } catch {
      toast.error("Could not send your inquiry. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-navy px-4 py-3.5 text-sm text-white placeholder:text-slate-500 focus:border-sun-bright focus:outline-none transition-colors";

  return (
    <div className="pt-40">
      <section className="mx-auto max-w-6xl px-6">
        <div className="grid gap-14 lg:grid-cols-[1.15fr_1fr] items-center">
          <Reveal>
            <Overline>Group Company</Overline>
            <h1 data-testid="cg-heading" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              {COMPANY.name.split(" Pvt. Ltd.")[0]}
              <span className="block text-sun-bright">Pvt. Ltd.</span>
            </h1>
            <p className="mt-7 text-base leading-relaxed text-slate-300">
              The corporate house behind the <span className="text-sun-bright font-semibold">Ananya Gold</span> brand.
              We partner with small manufacturers, repackage their goods under our own branding, and
              sell finished personal-care and home-care products through our trade network.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-400">
              For manufacturers, we are the fastest route from factory floor to retail shelf. For
              distributors and shop owners, we are a single, accountable branded supplier.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-sun/25 bg-sun/5 p-10 flex flex-col items-center text-center">
              <span className="inline-block bg-white rounded-2xl px-8 py-5 shadow-xl">
                <img src={ASSETS.logoGold} alt="Ananya Gold" className="h-20 w-auto" />
              </span>
              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.28em] text-sun-bright">
                An Ananya Gold Enterprise
              </p>
              <div className="mt-6 flex flex-col gap-2.5 text-sm text-slate-300">
                <span className="inline-flex items-center gap-2"><ShieldCheck size={15} className="text-lime" /> CIN: U46309MP2026PTC087167</span>
                <span className="inline-flex items-center gap-2"><CalendarDays size={15} className="text-lime" /> As On: 24th September 2026</span>
                <span className="inline-flex items-center gap-2"><BadgeCheck size={15} className="text-lime" /> MSME-registered trading house</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-24">
        <Reveal>
          <div className="grid gap-6 md:grid-cols-3" data-testid="cg-facts">
            {FACTS.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.1}>
                <div data-testid={f.testid} className="rounded-3xl border border-white/10 bg-navy-card p-8 transition-colors duration-500 hover:border-sun/50">
                  <f.icon size={24} className="text-sun-bright" />
                  <p className="mt-5 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">{f.label}</p>
                  <p className="mt-2 font-display text-xl sm:text-2xl font-bold tracking-tight">{f.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Reveal>
      </section>
      
      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <Overline>The People</Overline>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Founded by partners, run like family.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2" data-testid="cg-founders">
          {CG_FOUNDERS.map((f, i) => (
            <Reveal key={f.name} delay={i * 0.12}>
              <div className="group h-full rounded-3xl border border-white/10 bg-navy-card p-10 transition-all duration-500 hover:border-lime/50 hover:-translate-y-1.5">
                <span className="inline-flex rounded-2xl bg-sun/10 p-4 text-sun-bright transition-colors duration-500 group-hover:bg-lime group-hover:text-navy">
                  <Sprout size={26} />
                </span>
                <h3 className="mt-7 font-display text-2xl font-bold">{f.name}</h3>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.22em] text-lime">{f.role}</p>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">{f.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <Overline>How We Work</Overline>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            From small factories to branded shelves — in three steps.
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {MODEL.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.12}>
              <div data-testid={`cg-model-step-${i + 1}`} className="group h-full rounded-3xl border border-white/10 bg-navy-card p-8 transition-all duration-500 hover:border-sun/50 hover:-translate-y-2">
                <div className="flex items-start justify-between">
                  <span className="font-display text-5xl font-extrabold text-stroke-lime">{m.n}</span>
                  <m.icon size={26} className="text-sun-bright transition-transform duration-500 group-hover:scale-125 group-hover:rotate-12" />
                </div>
                <h3 className="mt-8 font-display text-xl font-bold">{m.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <div className="grid gap-12 lg:grid-cols-2 items-center rounded-[2.5rem] border border-white/10 bg-navy-card overflow-hidden">
            <div className="p-10 sm:p-14">
              <Overline>For Manufacturers</Overline>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                Make the product. We'll build the brand.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-400">
                If you are a small manufacturer of soaps, detergents, cleaners or personal-care goods,
                we can take your output to market under the Ananya Gold label — with repackaging,
                branding and distribution handled by us.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="#manufacturer-form"
                  data-testid="cg-manufacturer-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-sun-bright px-7 py-4 text-sm font-bold text-navy transition-all duration-300 hover:bg-lime hover:gap-3"
                >
                  Partner as a Manufacturer <ArrowUpRight size={16} />
                </a>
                <a
                  href={`mailto:${CONTACT.altEmail}`}
                  data-testid="cg-email-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-bold text-white transition-colors duration-300 hover:border-sun-bright hover:text-sun-bright"
                >
                  {CONTACT.altEmail}
                </a>
              </div>
            </div>
            <div className="relative min-h-[340px] spotlight-card p-8 flex items-center justify-center">
              <img
                data-testid="cg-range-image"
                src="/assets/gold-handwash.jpg"
                alt="Ananya Gold personal care range"
                className="max-h-[320px] w-auto object-contain drop-shadow-2xl rotate-2 transition-transform duration-700 hover:rotate-0"
              />
            </div>
          </div>
        </Reveal>
      </section>

      <section id="manufacturer-form" className="mx-auto max-w-6xl px-6 pt-28">
        <Reveal>
          <div className="rounded-[2.5rem] border border-sun/25 bg-navy-card p-8 sm:p-12">
            <div className="max-w-2xl">
              <Overline>Manufacturer Inquiry Form</Overline>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                Tell us what you make.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-400">
                Share your unit details and our sourcing team will evaluate fit, volumes and
                repackaging terms — usually within 3 working days.
              </p>
            </div>
            <form onSubmit={handleSubmit} data-testid="manufacturer-form" className="mt-10 grid gap-4 sm:grid-cols-2">
              <input data-testid="mfr-form-name" required placeholder="Your name *" className={inputCls} value={form.name} onChange={set("name")} />
              <input data-testid="mfr-form-factory" required placeholder="Factory / unit name *" className={inputCls} value={form.factory_name} onChange={set("factory_name")} />
              <input data-testid="mfr-form-location" required placeholder="Factory location *" className={inputCls} value={form.location} onChange={set("location")} />
              <select data-testid="mfr-form-type" required className={`${inputCls} appearance-none`} value={form.product_type} onChange={set("product_type")}>
                <option>Soaps & Bathing Bars</option>
                <option>Detergents & Laundry</option>
                <option>Home Care & Cleaners</option>
                <option>Liquid Care (Hand Wash etc.)</option>
                <option>Food & Snacks</option>
                <option>Other FMCG</option>
              </select>
              <input data-testid="mfr-form-phone" required placeholder="Phone *" className={inputCls} value={form.phone} onChange={set("phone")} />
              <input data-testid="mfr-form-email" required type="email" placeholder="Email *" className={inputCls} value={form.email} onChange={set("email")} />
              <input data-testid="mfr-form-capacity" placeholder="Monthly capacity (optional)" className={`${inputCls} sm:col-span-2`} value={form.monthly_capacity} onChange={set("monthly_capacity")} />
              <textarea
                data-testid="mfr-form-message"
                placeholder="Tell us about your products, certifications and current buyers (optional)"
                rows={4}
                className={`${inputCls} resize-none sm:col-span-2`}
                value={form.message}
                onChange={set("message")}
              />
              <button
                type="submit"
                data-testid="mfr-form-submit"
                disabled={loading}
                className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-sun-bright px-8 py-4 text-sm font-bold text-navy transition-colors hover:bg-lime disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending…" : "Submit Manufacturer Inquiry"}
              </button>
              <p className="sm:col-span-2 text-xs text-slate-500 text-center">
                Your details go straight to our sourcing team. No spam, ever.
              </p>
            </form>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-sun px-8 py-14 sm:px-16 text-navy">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/30 blur-3xl" />
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-2xl leading-tight">
              Explore the Ananya Gold range.
            </h2>
            <p className="mt-4 max-w-xl text-base font-medium text-navy/80">
              Soaps, hand wash and home-care essentials — branded, packed and ready for your shelves.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/products?brand=gold"
                data-testid="cg-explore-range-cta"
                className="inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:gap-3 hover:bg-navy-deep"
              >
                View Gold Products <ArrowUpRight size={17} />
              </Link>
              <a
                href="/downloads/ananya-gold-trade-sheet.pdf"
                download
                data-testid="cg-trade-sheet-btn"
                className="inline-flex items-center gap-2 rounded-full border-2 border-navy px-8 py-4 text-sm font-bold text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
              >
                <Download size={16} /> Trade Sheet (PDF)
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
